import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ShoppingCart, Trash2, CreditCard, Plus, FileText } from 'lucide-react';
import { useCart } from '@/hooks/useCart';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { computeDiscountedPrice } from '@/utils/pricing';
import { formatPriceFromCents } from '@/utils/formatPrice';
import { PaymentRouter } from './PaymentRouter';
import QuoteRequestModal from '@/components/quote/QuoteRequestModal';
import { logger } from '@/lib/logger';

export function CartSummary() {
  const { items, removeItem, clearCart, getTotalPrice, getItemsCount } = useCart();
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [showPaymentOptions, setShowPaymentOptions] = useState(false);
  const [recalculatedItems, setRecalculatedItems] = useState<any[]>([]);

  // Recalculate prices for all cart items to apply current promotions
  useEffect(() => {
    const recalculatePrices = async () => {
      if (items.length === 0) return;

      try {
        const updatedItems = await Promise.all(
          items.map(async (item) => {
            try {
              const { data: courseData, error } = await supabase
                .rpc('get_public_course_info')
                .eq('id', item.id)
                .maybeSingle();

              if (error || !courseData) {
                logger.warn('Could not fetch updated price', { itemId: item.id, error });
                return item;
              }

              const originalPrice = Number(courseData.price_ttc ?? 0);
              const promotions = Array.isArray(courseData.promotions) ? courseData.promotions : 
                                (typeof courseData.promotions === 'string' ? JSON.parse(courseData.promotions) : []);
              const pricingInfo = computeDiscountedPrice(originalPrice, promotions);

              return {
                ...item,
                actualPrice: pricingInfo.discountedPrice,
                originalPrice: originalPrice,
                hasDiscount: pricingInfo.hasDiscount,
                savings: pricingInfo.savings
              };
            } catch (error) {
              console.error('Error recalculating price for item', item.id, error);
              return item;
            }
          })
        );

        setRecalculatedItems(updatedItems);
      } catch (error) {
        console.error('Error recalculating cart prices:', error);
        setRecalculatedItems(items);
      }
    };

    recalculatePrices();
  }, [items]);

  // Calculate total with updated prices
  const getActualTotalPrice = () => {
    return recalculatedItems.reduce((total, item) => total + (item.actualPrice || item.price), 0);
  };

  const formatPriceDisplay = (price: number, currency: string = 'EUR') => {
    return formatPriceFromCents(price, currency);
  };

  const handleStripePayment = async () => {
    if (!user) {
      toast({
        title: "Connexion requise",
        description: "Vous devez être connecté pour effectuer un paiement",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-cart-checkout', {
        body: {
          items: items.map(item => ({
            courseId: item.id,
            formationTitle: item.title,
            amount: Math.round(item.price), // Already in cents from database
            currency: item.currency
          })),
          totalAmount: Math.round(getTotalPrice()), // Already in cents from database
          currency: items[0]?.currency || 'eur'
        }
      });

      if (error) throw error;

      if (data?.url) {
        // Ouvrir Stripe checkout dans un nouvel onglet
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error('Erreur lors du paiement:', error);
      toast({
        title: "Erreur",
        description: "Impossible d'initier le paiement. Veuillez réessayer.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (getItemsCount() === 0) {
    return (
      <Card className="w-full max-w-md">
        <CardContent className="flex flex-col items-center justify-center py-8">
          <ShoppingCart className="h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground text-center">
            Votre panier est vide
          </p>
          <p className="text-sm text-muted-foreground text-center mt-2">
            Ajoutez des formations pour commencer
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShoppingCart className="h-5 w-5" />
          Mon Panier
          <Badge variant="secondary">{getItemsCount()}</Badge>
        </CardTitle>
        <CardDescription>
          {getItemsCount()} formation{getItemsCount() > 1 ? 's' : ''} sélectionnée{getItemsCount() > 1 ? 's' : ''}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Liste des formations */}
        <div className="space-y-3">
          {recalculatedItems.map((item) => (
            <div key={item.id} className="flex items-start justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex-1">
                <h4 className="font-medium text-sm">{item.title}</h4>
                {item.hasDiscount ? (
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-lg font-bold text-primary">
                      {formatPriceDisplay(item.actualPrice, item.currency)}
                    </span>
                    <span className="text-sm text-muted-foreground line-through">
                      {formatPriceDisplay(item.originalPrice, item.currency)}
                    </span>
                  </div>
                ) : (
                  <p className="text-lg font-bold text-primary mt-1">
                    {formatPriceDisplay(item.actualPrice || item.price, item.currency)}
                  </p>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeItem(item.id)}
                className="text-red-500 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>

        <Separator />

        {/* Total */}
        <div className="flex justify-between items-center">
          <span className="font-semibold">Total</span>
          <span className="text-2xl font-bold text-primary">
            {formatPriceDisplay(getActualTotalPrice(), items[0]?.currency || 'eur')}
          </span>
        </div>

        {/* Actions */}
        <div className="space-y-2">
          <Dialog open={showPaymentOptions} onOpenChange={setShowPaymentOptions}>
            <DialogTrigger asChild>
              <Button className="w-full" size="lg">
                <CreditCard className="h-4 w-4 mr-2" />
                Procéder au paiement
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Options de paiement</DialogTitle>
                <DialogDescription>
                  Choisissez votre mode de paiement pour {getItemsCount()} formation{getItemsCount() > 1 ? 's' : ''}
                </DialogDescription>
              </DialogHeader>
              <PaymentRouter
                courseId="cart" // Identifiant spécial pour le panier
                formationTitle={`Panier de ${getItemsCount()} formation${getItemsCount() > 1 ? 's' : ''}`}
                price={getActualTotalPrice()}
                currency={items[0]?.currency || 'eur'}
                isCart={true}
                cartItems={recalculatedItems.map(item => ({
                  id: item.id,
                  title: item.title,
                  price: item.actualPrice || item.price,
                  currency: item.currency
                }))}
              />
            </DialogContent>
          </Dialog>
          
          {/* Bouton Devis Panier */}
          <QuoteRequestModal
            courseId="cart"
            courseTitle={`Panier de ${getItemsCount()} formation${getItemsCount() > 1 ? 's' : ''}`}
            coursePrice={getActualTotalPrice()}
            courseDuration={4} // Durée par défaut
            trigger="cart.export.quote"
            cartItems={items}
          >
            <Button variant="outline" className="w-full">
              <FileText className="h-4 w-4 mr-2" />
              Télécharger un devis
            </Button>
          </QuoteRequestModal>
          
          <Button 
            variant="outline" 
            onClick={clearCart}
            className="w-full"
          >
            Vider le panier
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}